import { BBox } from './bbox';

/**
 * Highest zoom level a feature tile may be requested at.
 */
export const FEATURE_TILE_MAX_ZOOM = 16;

/**
 * Zoom levels clients request feature tiles at. Quantized in steps of 2 so a viewport
 * covers a handful of tiles at any view zoom, and tiles are reused across small zoom changes.
 */
export const FEATURE_TILE_ZOOM_LEVELS = [2, 4, 6, 8, 10, 12, 14] as const;

/**
 * Maximum number of tiles per batched feature tile request; clients split larger views into
 * several requests.
 */
export const FEATURE_TILE_BATCH_MAX = 16;

/**
 * Latitude at which the Web Mercator tile grid ends (the world is square up to here). Higher
 * latitudes are still on the map, so the first and last tile rows extend to the poles.
 */
const WEB_MERCATOR_MAX_LATITUDE = 85.0511287798066;

/**
 * A slippy-map (XYZ, Web Mercator) tile coordinate.
 */
export class MapTile {
  readonly z: number;
  readonly x: number;
  readonly y: number;

  /**
   * @throws When the coordinates are not integers or out of range for the zoom level
   */
  constructor(z: number, x: number, y: number) {
    this.z = z;
    this.x = x;
    this.y = y;

    this.validate();
  }

  /**
   * Parses a "z/x/y" key; throws when it is malformed or out of range.
   */
  static fromKey(key: string): MapTile {
    const parts = key.split('/');

    if (parts.length !== 3 || parts.some(part => !/^\d+$/.test(part))) {
      throw new Error(`Invalid tile key: ${key}`);
    }

    const [z, x, y] = parts.map(Number) as [number, number, number];

    return new MapTile(z, x, y);
  }

  /**
   * The tiles at zoom z covering a WGS84 bbox.
   */
  static forBBox(bbox: BBox, z: number): MapTile[] {
    const minX = MapTile.longitudeToX(bbox.minLongitude, z);
    const maxX = MapTile.longitudeToX(bbox.maxLongitude, z);
    // Tile y grows southwards, so the north edge gives the smallest y.
    const minY = MapTile.latitudeToY(bbox.maxLatitude, z);
    const maxY = MapTile.latitudeToY(bbox.minLatitude, z);
    const tiles: MapTile[] = [];

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        tiles.push(new MapTile(z, x, y));
      }
    }

    return tiles;
  }

  /**
   * The quantized feature tile zoom for a map view zoom: the largest level at least one
   * below the view zoom, clamped to the available levels. A tile then renders 512-2048px
   * wide, which keeps overfetch beyond the viewport low while a viewport still spans only a
   * handful of tiles.
   */
  static featureTileZoomForViewZoom(viewZoom: number): number {
    const target = viewZoom - 1;
    let zoom: number = FEATURE_TILE_ZOOM_LEVELS[0];

    for (const level of FEATURE_TILE_ZOOM_LEVELS) {
      if (level <= target) {
        zoom = level;
      }
    }

    return zoom;
  }

  /**
   * The x of the tile containing a longitude at zoom z, clamped to the world.
   */
  private static longitudeToX(longitude: number, z: number): number {
    const tileCount = 2 ** z;
    const x = Math.floor(((longitude + 180) / 360) * tileCount);

    return Math.min(Math.max(x, 0), tileCount - 1);
  }

  /**
   * The y of the tile containing a latitude at zoom z. Latitudes beyond the Web Mercator
   * range fall in the first or last row, which extend to the poles (see getBBox).
   */
  private static latitudeToY(latitude: number, z: number): number {
    const tileCount = 2 ** z;
    const clamped = Math.min(Math.max(latitude, -WEB_MERCATOR_MAX_LATITUDE), WEB_MERCATOR_MAX_LATITUDE);
    const radians = (clamped * Math.PI) / 180;
    const y = Math.floor(((1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2) * tileCount);

    return Math.min(Math.max(y, 0), tileCount - 1);
  }

  /**
   * The longitude of a tile's western edge.
   */
  private static xToLongitude(x: number, z: number): number {
    return (x / 2 ** z) * 360 - 180;
  }

  /**
   * The latitude of a tile's northern edge.
   */
  private static yToLatitude(y: number, z: number): number {
    const n = Math.PI * (1 - (2 * y) / 2 ** z);

    return (Math.atan(Math.sinh(n)) * 180) / Math.PI;
  }

  /**
   * Stable key, e.g. for caching: "z/x/y".
   */
  get key(): string {
    return `${this.z}/${this.x}/${this.y}`;
  }

  /**
   * The WGS84 bbox this tile covers. The first and last rows extend to the poles: the map can
   * show and draw beyond the Web Mercator grid's ±85.05°, and features there must still
   * belong to a tile.
   */
  getBBox(): BBox {
    const lastRow = 2 ** this.z - 1;

    return new BBox(
      MapTile.xToLongitude(this.x, this.z),
      this.y === lastRow ? -90 : MapTile.yToLatitude(this.y + 1, this.z),
      MapTile.xToLongitude(this.x + 1, this.z),
      this.y === 0 ? 90 : MapTile.yToLatitude(this.y, this.z),
    );
  }

  /**
   * Throws when the tile is invalid (see isValid).
   */
  validate(): void {
    if (!this.isValid()) {
      throw new Error(`Invalid tile: z=${this.z}, x=${this.x}, y=${this.y}`);
    }
  }

  /**
   * Whether the coordinates are integers, the zoom is within FEATURE_TILE_MAX_ZOOM and x/y
   * fall within the zoom level's grid.
   */
  isValid(): boolean {
    if (![this.z, this.x, this.y].every(Number.isInteger)) {
      return false;
    }

    if (this.z < 0 || this.z > FEATURE_TILE_MAX_ZOOM) {
      return false;
    }

    return this.isWithinGrid();
  }

  /**
   * Whether x and y fall within the zoom level's grid.
   */
  private isWithinGrid(): boolean {
    const tileCount = 2 ** this.z;

    return this.x >= 0 && this.x < tileCount && this.y >= 0 && this.y < tileCount;
  }
}

/**
 * A draw layer to get feature tiles of, with the features generation of the tiles the client
 * already holds, in the feature tile endpoint's "<drawLayerId>" or "<drawLayerId>:<generation>"
 * entry format. Clients build entries with it and the API parses them with it, so both sides
 * always agree on the format.
 */
export class KnownDrawLayerGeneration {
  constructor(
    readonly drawLayerId: string,
    readonly generation?: number,
  ) {}

  /**
   * Parses an entry; undefined when it is malformed.
   */
  static parse(entry: string): KnownDrawLayerGeneration | undefined {
    const separatorIndex = entry.indexOf(':');

    if (separatorIndex === -1) {
      return entry.length > 0 ? new KnownDrawLayerGeneration(entry) : undefined;
    }

    const drawLayerId = entry.slice(0, separatorIndex);
    const generation = entry.slice(separatorIndex + 1);

    if (drawLayerId.length === 0 || !/^\d+$/.test(generation)) {
      return undefined;
    }

    return new KnownDrawLayerGeneration(drawLayerId, Number(generation));
  }

  /**
   * The entry as sent to the feature tile endpoint.
   */
  get entry(): string {
    return this.generation === undefined ? this.drawLayerId : `${this.drawLayerId}:${this.generation}`;
  }
}
